/**
 * Money helpers shared by the web app (and usable by the API). Amounts travel as decimal strings and are
 * computed with BigInt, never floats, so results match the database's NUMERIC arithmetic exactly.
 */

/** Base-currency amounts 1-50,000 use rate1 (retail); 50,001+ use rate2 (wholesale): the two tiers stored per currency per day. */
export const RATE_TIER_LIMIT = 50_000;

/** Parses a non-negative decimal string into an integer scaled by 10^scale. Null if malformed or too precise. */
export function toScaled(value: string | number | null | undefined, scale: number): bigint | null {
  if (value === null || value === undefined) return null;
  // Same shape the API accepts: digits, optional fraction with at least one digit (no ".5", no "5.")
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(String(value).trim().replace(/,/g, ''));
  if (!m) return null;
  const frac = m[3] ?? '';
  if (frac.length > scale) return null;
  const n = BigInt(m[2]! + frac.padEnd(scale, '0'));
  return m[1] === '-' ? -n : n;
}

const fromScaled = (n: bigint, scale: number): string => {
  const neg = n < 0n;
  const abs = neg ? -n : n;
  const s = abs.toString().padStart(scale + 1, '0');
  const int = s.slice(0, s.length - scale);
  const frac = s.slice(s.length - scale);
  return `${neg ? '-' : ''}${int}${scale ? `.${frac}` : ''}`;
};

/**
 * Agent invoice lines (2026-10-10): amount (2 dp) / rate (8 dp), rounded half-up to THREE decimals, as the database's generated
 * column `round(amount / rate, 3)` does. The rate is the base currency per 1 unit of the agent's currency.
 */
export function lineTotal(amount: string, rate: string): string | null {
  const a = toScaled(amount, 2);
  const r = toScaled(rate, 8);
  if (a === null || r === null || a <= 0n || r <= 0n) return null;
  // (a/100) / (r/10^8) units = a*10^6 / r units = a*10^9 / r thousandths; add half the divisor to round half-up
  const thousandths = (a * 1_000_000_000n * 2n + r) / (2n * r);
  return fromScaled(thousandths, 3);
}

/**
 * amount (2 dp) x rate (4 dp), rounded half-up to 2 dp, as the database's generated column does.
 * Purchase Orders only: their exchange rate keeps 4 decimals, while Agent Invoice lines use
 * lineTotal (8 decimals, a separate column and helper).
 */
export function purchaseTotal(amount: string, rate: string): string | null {
  const a = toScaled(amount, 2);
  const r = toScaled(rate, 4);
  if (a === null || r === null || a <= 0n || r <= 0n) return null;
  // a is amount in hundredths, r is rate in ten-thousandths; (a/100)*(r/10000) in cents = a*r/10000
  const cents = (a * r + 5_000n) / 10_000n;
  return fromScaled(cents, 2);
}

/** Exact sum of decimal strings with `scale` decimals (default 2); invalid entries count as zero. */
export function sumMoney(values: (string | null | undefined)[], scale = 2): string {
  return fromScaled(values.reduce<bigint>((acc, v) => acc + (toScaled(v, scale) ?? 0n), 0n), scale);
}

/** Drops pointless trailing zeros from a rate: "560.0000" -> "560", "438.7000" -> "438.7". */
export function trimRate(rate: string): string {
  return rate.includes('.') ? rate.replace(/\.?0+$/, '') : rate;
}

/** The rate to pre-fill for an amount: retail (rate1) up to 50,000, wholesale (rate2) above. Null if unknown. */
export function suggestRate(
  amountInr: string,
  rates: { rate1?: string | null; rate2?: string | null } | null | undefined,
): string | null {
  const a = toScaled(amountInr, 2);
  if (a === null || a <= 0 || !rates) return null;
  const pick = a <= BigInt(RATE_TIER_LIMIT) * 100n ? rates.rate1 : rates.rate2;
  return pick ? trimRate(pick) : null;
}

/** Display formatting only ("1,234.50"); keeps at least `min` and at most `max` decimals. Non-numbers pass through. */
export function formatMoney(value: string | number | null | undefined, min = 2, max = 2): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-US', { minimumFractionDigits: min, maximumFractionDigits: max });
}
